import { gql, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { SiteOption, BuildingOption, FloorOption } from './useSiteHierarchyFilters';
import { useSpaceAccess } from '@/contexts/SpaceAccessContext';

export const GET_SITE_HIERARCHY = gql`
  query GetSiteHierarchy {
    sites {
      data {
        id
        name
        buildings {
          id
          name
          floors {
            id
            name
            rooms {
              id
              name
              floorId: floorID
            }
            zones {
              id
              name
              floorId: floorID
              roomId: roomID
            }
          }
        }
      }
    }
  }
`;

/**
 * Filter hierarchy data based on user's space access assignments
 */
function filterHierarchyByAccess(
  sites: SiteOption[],
  hasAccess: (
    spaceType: 'site' | 'building' | 'floor' | 'room' | 'zone',
    spaceId: string,
  ) => boolean,
  isUnrestricted: boolean,
): SiteOption[] {
  // If user is admin/superuser, return all sites
  if (isUnrestricted) {
    return sites;
  }

  // Filter sites based on access
  return sites
    .map((site) => {
      // Check if user has access to the site
      const hasSiteAccess = hasAccess('site', site.id);

      // Filter buildings
      const filteredBuildings = site.buildings
        .map((building) => {
          // Check if user has access to the building
          const hasBuildingAccess = hasSiteAccess || hasAccess('building', building.id);

          // Filter floors
          const filteredFloors = building.floors
            .map((floor) => {
              // Check if user has access to the floor
              const hasFloorAccess = hasBuildingAccess || hasAccess('floor', floor.id);

              // Filter rooms
              const filteredRooms = (floor.rooms || []).filter(
                (room) => hasFloorAccess || hasAccess('room', room.id),
              );

              // Filter zones
              const filteredZones = (floor.zones || []).filter(
                (zone) => hasFloorAccess || hasAccess('zone', zone.id),
              );

              // Include floor if user has access to it, or any rooms/zones within it
              if (hasFloorAccess || filteredRooms.length > 0 || filteredZones.length > 0) {
                return {
                  ...floor,
                  rooms: filteredRooms,
                  zones: filteredZones,
                } as FloorOption;
              }
              return null;
            })
            .filter((f): f is FloorOption => f !== null);

          // Include building if user has access to it, or any floors within it
          if (hasBuildingAccess || filteredFloors.length > 0) {
            return {
              ...building,
              floors: filteredFloors,
            } as BuildingOption;
          }
          return null;
        })
        .filter((b): b is BuildingOption => b !== null);

      // Include site if user has access to it, or any buildings within it
      if (hasSiteAccess || filteredBuildings.length > 0) {
        return {
          ...site,
          buildings: filteredBuildings,
        } as SiteOption;
      }
      return null;
    })
    .filter((s): s is SiteOption => s !== null);
}

/**
 * useSiteOptionsQuery fetches the site hierarchy and returns processed site options, loading, and error states.
 * The results are filtered based on user's space access assignments.
 */
export function useSiteOptionsQuery({ skip = false } = {}) {
  const { loading, error, data } = useQuery(GET_SITE_HIERARCHY, {
    skip,
  });

  const { hasAccess, isUnrestricted, isLoading: isAccessLoading } = useSpaceAccess();

  // Filter site options based on user's space access
  const siteOptions = useMemo(() => {
    const allSites = (data?.sites?.data || []) as SiteOption[];

    // Wait for access data to load before filtering
    if (isAccessLoading) {
      return [];
    }

    return filterHierarchyByAccess(allSites, hasAccess, isUnrestricted);
  }, [data?.sites?.data, hasAccess, isUnrestricted, isAccessLoading]);

  return {
    siteOptions,
    loading: loading || isAccessLoading,
    error,
  };
}
